import Errors from "../../utilities/constants/Errors";
import HttpClient from "../../utilities/functions/HttpClient";
import CustomToast from "../../utilities/components/CustomToast";
import {
  INVOICES_ENDPOINT,
  RESERVATIONS_ENDPOINT,
  USERS_ENDPOINT,
} from "../../utilities/constants/ServerEndPoints";

const normalizeInvoiceQueryParams = (params = {}) => {
  const normalizedParams = { ...params };

  if (normalizedParams.pageSize != null && normalizedParams.size == null) {
    normalizedParams.size = normalizedParams.pageSize;
  }

  delete normalizedParams.pageSize;

  return normalizedParams;
};

const getBackendErrorMessage = (err) => {
  const data = err?.response?.data;

  if (Array.isArray(data?.errors) && data.errors.length > 0) {
    return data.errors[0];
  }

  return data?.message || null;
};

const handleInvoiceRequestError = (err) => {
  const backendMessage = getBackendErrorMessage(err);

  switch (err?.response?.status) {
    case 400:
      CustomToast(
        backendMessage || "Please review your data and try again.",
        "error",
      );
      return;
    case 401:
      CustomToast("Please sign in to continue.", "error");
      return;
    case 403:
      CustomToast(
        backendMessage || "You are not allowed to perform this action.",
        "error",
      );
      return;
    case 404:
      CustomToast(backendMessage || Errors.NOT_FOUND_ERROR, "info");
      return;
    case 503:
      CustomToast(Errors.SERVICE_UNAVAILABLE_ERROR, "error");
      return;
    default:
      CustomToast(backendMessage || Errors.API_ERROR, "error");
  }
};

/**
 * Get the invoices of a user.
 * @param {number} userId The id of the user.
 * @param {object} params The parameters to filter the invoices.
 * @param {function} setInvoiceData The function to set the invoices.
 * @param {function} setIsLoading The function to set the loading state.
 * @returns The invoices of a user.
 */
export const getInvoicesByUserId = async (
  userId,
  params,
  setInvoiceData,
  setIsLoading,
) => {
  try {
    const normalizedParams = normalizeInvoiceQueryParams(params);
    const response = await HttpClient.getAxiosClient().get(
      `${USERS_ENDPOINT}/${userId}${INVOICES_ENDPOINT}`,
      { params: normalizedParams },
    );

    if (response.status === 200) {
      setInvoiceData(response.data);
    }
  } catch (err) {
    handleInvoiceRequestError(err);
  } finally {
    setIsLoading(false);
  }
};

export const hydrateInvoiceForView = async ({
  invoice,
  userId,
  getUserById,
}) => {
  const [reservation, user] = await Promise.all([
    (async () => {
      if (!invoice?.reservationId) {
        return null;
      }

      try {
        const response = await HttpClient.getAxiosClient().get(
          `${USERS_ENDPOINT}/${userId}${RESERVATIONS_ENDPOINT}`,
          {
            params: {
              id: invoice.reservationId,
              page: 0,
              size: 1,
            },
          },
        );

        return response.data?.content?.[0] ?? null;
      } catch (err) {
        handleInvoiceRequestError(err);
        return null;
      }
    })(),
    new Promise((resolve) => {
      if (!invoice?.userId) {
        resolve(null);
        return;
      }

      getUserById(invoice.userId, resolve);
    }),
  ]);

  return {
    ...invoice,
    reservation: reservation || invoice.reservation,
    user: user || invoice.user,
  };
};
